import {Autocomplete} from '@mantine/core';
import {useEffect, useRef, useState} from 'react';

type Props = {
  companyLogoUrl?: string;
  companyName: string;
  onClick?: () => void;
  editable?: boolean;
  /** 企業名保存ハンドラー。成功時はtrue、失敗時はfalseを返す */
  onSave?: (companyName: string, companyId?: number) => Promise<boolean>;
  companyList?: {id: number; value: string}[];
};

export const CompanyNameText: React.FC<Props> = ({
  companyLogoUrl,
  companyName,
  onClick,
  editable = false,
  onSave,
  companyList = [],
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(companyName);
  const inputRef = useRef<HTMLInputElement>(null);
  const previousValueRef = useRef(companyName);

  useEffect(() => {
    setLocalValue(companyName);
    previousValueRef.current = companyName;
  }, [companyName]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (editable) {
      setIsEditing(true);
    } else if (onClick) {
      onClick();
    }
  };

  const handleBlur = async () => {
    setIsEditing(false);
    if (localValue !== previousValueRef.current) {
      // 入力値に完全一致する企業を検索してIDを決定
      // 見つからない場合はundefined（企業IDがリセットされる）
      const matchedCompany = companyList.find(
        (company) => company.value === localValue,
      );

      const success = onSave
        ? await onSave(localValue, matchedCompany?.id)
        : true;

      if (success) {
        previousValueRef.current = localValue;
      } else {
        // 保存失敗時は元の値に戻す
        setLocalValue(previousValueRef.current);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
    if (e.key === 'Escape') {
      setLocalValue(previousValueRef.current);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div className="flex-1 pc:w-[350px]">
        <Autocomplete
          ref={inputRef}
          value={localValue}
          onChange={setLocalValue}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="企業名を入力"
          data={companyList}
          onItemSubmit={(item) => {
            setLocalValue(item.value);
          }}
          maxDropdownHeight={320}
          limit={20}
          filter={(value, item) =>
            item.value.toLowerCase().includes(value.toLowerCase().trim())
          }
          size="sm"
          classNames={{
            input: 'text-base font-bold py-8',
          }}
        />
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-4 max-w-[350px] ${
        editable || onClick ? 'cursor-pointer hover:opacity-80' : ''
      }`}
      onClick={handleClick}
      role={editable || onClick ? 'button' : undefined}
      tabIndex={editable || onClick ? 0 : undefined}
      onKeyDown={
        editable || onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
    >
      {companyLogoUrl && (
        <img
          src={companyLogoUrl}
          alt={`${companyName}のロゴ`}
          className="w-8 h-8 object-contain rounded"
        />
      )}
      <div className="font-bold text-base line-clamp-2 pc:line-clamp-1">
        {companyName ||
          (editable && <span className="text-black-3">企業名を入力</span>)}
      </div>
    </div>
  );
};
